const DEFAULT_WS_URL = 'wss://app.biel.ai/ws/messages';

export class BielWebSocketService {
  private ws: WebSocket;
  private messageHandlers: ((message: any) => void)[] = [];
  private isConnected: boolean = false;
  private projectId: string;
  private chatId: string = '';
  private email: string = '';
  private apiKey: string = '';
  private errorMessage: string;
  private loadingStartHandler: () => void;
  private loadingEndHandler: () => void;
  private connectionStatusHandlers: ((isConnected: boolean) => void)[] = [];
  private wsUrl: string;
  private thinkMode: boolean = false;
  private metadata: string = '';

  constructor(
    projectId: string,
    errorMessage: string,
    email: string,
    apiKey: string = '',
    baseUrl: string = '',
    initialChatId: string = '',
    thinkMode: boolean = false,
    metadata: string = ''
  ) {
    this.projectId = projectId;
    this.errorMessage = errorMessage;
    this.email = email;
    this.apiKey = apiKey;
    this.chatId = initialChatId;
    this.thinkMode = thinkMode;
    this.metadata = metadata;

    // Convert HTTP/HTTPS base URL to WebSocket URL
    if (baseUrl) {
      const httpUrl = baseUrl.replace('/api/v1', ''); // Remove API path
      this.wsUrl =
        httpUrl.replace('http://', 'ws://').replace('https://', 'wss://') + '/ws/messages';
    } else {
      this.wsUrl = DEFAULT_WS_URL;
    }

    this.connect();
  }

  private connect(): void {
    let wsUrl: string;

    if (this.chatId) {
      // Use specific chat route for existing conversations
      wsUrl = `${this.wsUrl}/chat/${this.chatId}/`;
    } else {
      // Use project route for new conversations
      wsUrl = `${this.wsUrl}/${this.projectId}/`;
    }

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      this.isConnected = true;
      this.connectionStatusHandlers.forEach((handler) => handler(true));
    };

    this.ws.onmessage = (event) => {
      const message = JSON.parse(event.data);

      if (!this.chatId && message.chatId) {
        this.chatId = message.chatId;
      }

      if (message.from) {
        // Always include the current chatId in the message
        const messageWithChatId = {
          ...message,
          chatId: this.chatId || message.chatId,
        };

        this.loadingEndHandler();
        this.messageHandlers.forEach((handler) => handler(messageWithChatId));
      }
    };

    this.ws.onclose = () => {
      this.isConnected = false;
      this.connectionStatusHandlers.forEach((handler) => handler(false));
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.isConnected = false;
      this.connectionStatusHandlers.forEach((handler) => handler(false));
      this.loadingEndHandler();
    };
  }

  public sendMessage(message: string): void {
    const payload = {
      projectId: this.projectId,
      chatId: this.chatId,
      url: window.location.href,
      email: this.email,
      from: 'user',
      message: message,
      thinkMode: this.thinkMode,
    };

    // Add API key to payload if provided
    if (this.apiKey) {
      payload['apiKey'] = this.apiKey;
    }

    // Add metadata to payload if provided
    if (this.metadata) {
      payload['metadata'] = this.metadata;
    }

    if (this.isConnected) {
      this.ws.send(JSON.stringify(payload));
      this.loadingStartHandler();
    } else {
      this.messageHandlers.forEach((handler) =>
        handler({ message: this.errorMessage, from: 'ai' })
      );
    }
  }

  public closeSocket(): void {
    this.ws.close();
  }

  public onMessage(handler: (message: any) => void): void {
    this.messageHandlers.push(handler);
  }

  public onLoadingStart(handler: () => void): void {
    this.loadingStartHandler = handler;
  }

  public onLoadingEnd(handler: () => void): void {
    this.loadingEndHandler = handler;
  }

  public onConnectionStatusChange(handler: (isConnected: boolean) => void): void {
    this.connectionStatusHandlers.push(handler);
    handler(this.isConnected);
  }

  public getChatId(): string {
    return this.chatId;
  }

  public isWebSocketConnected(): boolean {
    return this.isConnected;
  }

  public getConnectionInfo(): { isConnected: boolean; chatId: string; wsUrl: string } {
    return {
      isConnected: this.isConnected,
      chatId: this.chatId,
      wsUrl: this.ws?.url || '',
    };
  }

  public setThinkMode(enabled: boolean): void {
    this.thinkMode = enabled;
  }
}
